import cx from 'classnames';
import {template, find, map, isEmpty} from 'lodash';
import moment, {Moment} from 'moment';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {clientConnectApi} from 'api';
import {
    Checkbox, ConfirmModal, DateTimePicker, FilesManagerInput, Icon, Input, notifier, Select,
    TextArea, Wysiwyg, FileArea, FileOwner, CopyText, Button, Timer,
} from 'components';
import {SMS_MAX_LENGTH, WITH_LOGO_MESSAGES} from 'constants/client-connect';
import {UserContext} from 'context';
import {ClientConnectMessageName, ValidationCode} from 'enums';
import {useApiData, useIsChanged} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {ClientConnect, ClientConnectNotificationType} from 'types';
import {apiUtils, dateUtils, settingsUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS.SEND_MESSAGE_DIALOG;
const T_ERROR_KEYS = T_KEYS.ERRORS;

const MESSAGE_NAME_T_KEY = {
    [ClientConnectMessageName.Invite]: T_KEYS.MESSAGE_NAME.INVITE,
    [ClientConnectMessageName.ClientAppInstallIos]: T_KEYS.MESSAGE_NAME.IOS_INSTALL,
    [ClientConnectMessageName.ClientAppConfigIos]: T_KEYS.MESSAGE_NAME.IOS_CONFIG,
    [ClientConnectMessageName.ClientAppInstallAndroid]: T_KEYS.MESSAGE_NAME.ANDROID_INSTALL,
    [ClientConnectMessageName.ClientAppConfigAndroid]: T_KEYS.MESSAGE_NAME.ANDROID_CONFIG,
};

type Props = {
    open: boolean;
    connect: ClientConnect;
    onClose: (success: boolean, data?: {isScheduled: boolean, connect: ClientConnect}) => void;
};

export const ClientConnectMessageDialog: React.FC<Props> = ({open, connect, onClose}: Props) => {
    const {t} = useTranslation();

    const {settings} = useContext(UserContext);
    const {
        smsEnabled, emailEnabled, whatsappEnabled
    } = useMemo(() => settingsUtils.getClientConnectionMessageAvailability(settings), [settings]);

    const smsAvailable = useMemo(() => smsEnabled && !isEmpty(connect?.contacts?.phone), [connect, smsEnabled]);
    const emailAvailable = useMemo(() => emailEnabled && !isEmpty(connect?.contacts?.email), [connect, emailEnabled]);
    const whatsappAvailable = useMemo(() => whatsappEnabled && !isEmpty(connect?.contacts?.phone), [connect, whatsappEnabled]);

    const defaultNotificationType = useMemo(() => {
        if (smsAvailable) {
            return ClientConnectNotificationType.SMS;
        } else if (emailAvailable) {
            return ClientConnectNotificationType.Email;
        } else if (whatsappAvailable) {
            return ClientConnectNotificationType.Whatsapp;
        } else {
            return null;
        }
    }, [smsAvailable, emailAvailable, whatsappAvailable]);

    const [message, setMessage] = useState<string>('');
    const [initialMessage, setInitialMessage] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [throttlingDate, setThrottlingDate] = useState<Date>(null);
    const [scheduledDate, setScheduledDate] = useState<Moment | null>(null);
    const [notificationType, setNotificationType] = useState<ClientConnectNotificationType>(null);
    const isNotificationTypeChanged = useIsChanged(notificationType);
    const [messageName, setMessageName] = useState<ClientConnectMessageName | null>(null);
    const [logoUrl, setLogoUrl] = useState<string>('');

    const smsMessages = useMemo(() => settings.clientConnect.notification.sms?.messages || [], [settings]);
    const emailMessages = useMemo(() => settings.clientConnect.notification.email?.messages || [], [settings]);
    const whatsappMessages = useMemo(() => settings.clientConnect.notification.whatsapp?.messages || [], [settings]);
    const messages = useMemo(() => {
        if (notificationType === ClientConnectNotificationType.SMS) {
            return smsMessages;
        } else if (notificationType === ClientConnectNotificationType.Email) {
            return emailMessages;
        } else if (notificationType === ClientConnectNotificationType.Whatsapp) {
            return whatsappMessages;
        } else {
            return [];
        }
    }, [notificationType, smsMessages, emailMessages, whatsappMessages]);

    const {timeZone, dateFormat, timeFormat} = useMemo(() => settings.common, [settings]);

    const messageOptions = useMemo(() => map(messages, (msg) => ({
        id: msg.name,
        name: MESSAGE_NAME_T_KEY[msg.name] ? t(MESSAGE_NAME_T_KEY[msg.name]) : msg.name
    })), [messages, t]);

    const selectedMessageOption = useMemo(() =>
        find(messageOptions, ({id}) => id === messageName), [messageName, messageOptions]);

    const notificationTypeOptions = useMemo(() => [
        ...(smsAvailable ? [{
            id: ClientConnectNotificationType.SMS,
            name: t(T_KEYS.NOTIFICATION_TYPE.SMS)
        }] : []), ...(emailAvailable ? [{
            id: ClientConnectNotificationType.Email,
            name: t(T_KEYS.NOTIFICATION_TYPE.EMAIL)
        }] : []), ...(whatsappAvailable ? [{
            id: ClientConnectNotificationType.Whatsapp,
            name: t(T_KEYS.NOTIFICATION_TYPE.WHATSAPP)
        }] : [])
    ], [t, smsAvailable, emailAvailable, whatsappAvailable]);

    const selectedNotificationTypeOption = useMemo(() =>
        find(notificationTypeOptions, {id: notificationType}), [notificationType, notificationTypeOptions]);

    const isScheduled = useMemo(() => !!scheduledDate, [scheduledDate]);

    const recipientDetails = useMemo(() => {
        if (notificationType === ClientConnectNotificationType.SMS) {
            return connect?.contacts?.phone;
        }
        if (notificationType === ClientConnectNotificationType.Email) {
            return connect?.contacts?.email;
        }
        if (notificationType === ClientConnectNotificationType.Whatsapp) {
            return connect?.contacts?.phone;
        }
        return null;
    }, [connect?.contacts?.email, connect?.contacts?.phone, notificationType]);

    const resetState = useCallback(() => {
        setNotificationType(null);
        setMessage('');
        setInitialMessage('');
        setErrorMessage('');
        setScheduledDate(null);
        setMessageName(null);
        setLogoUrl('');
        setThrottlingDate(null);
    }, []);

    const closeModal = useCallback((success: boolean, _connect?: ClientConnect) => {
        resetState();

        if (success) {
            const notifierTitle = t(isScheduled ? T_KEYS.MESSAGE_WILL_BE_SENT : T_KEYS.MESSAGE_SENT);
            notifier.success({title: notifierTitle});
        }

        onClose(success, {isScheduled, connect: _connect});
    }, [t, isScheduled, resetState, onClose]);

    const onSuccess = useCallback((_connect: ClientConnect) => {
        closeModal(true, _connect);
    }, [closeModal]);

    const onError = useCallback((error) => {
        let {data} = error.response;
        const scheduledDateError = apiUtils.getFieldValidationError(error, 'scheduledDate');
        if (scheduledDateError?.code === ValidationCode.Duplicated) {
            data = 'DUPLICATE_SCHEDULED_DATE';
        } else if (data?.date?.code === ValidationCode.MinDate) {
            if (data.date.expected) {
                setThrottlingDate(new Date(data.date.expected));
            }
            data = 'THROTTLING_EXCEEDED';
        }
        const requestErrorMessage = T_ERROR_KEYS[data] || T_ERROR_KEYS.UNEXPECTED;
        setErrorMessage(requestErrorMessage);
    }, []);

    const {send: sendMessage, loading} = useApiData(clientConnectApi.notify, {
        onSuccess,
        onError
    });

    const messageTemplate = useMemo(() => find(messages, {name: messageName})?.template, [messages, messageName]);
    const messageSubject = useMemo(() => find(emailMessages, {name: messageName})?.subject, [emailMessages, messageName]);

    const refreshMessage = useCallback((tmpl: string) => {
        const templateFn = template(tmpl);
        const updatedMessage = templateFn({
            name: connect?.userInfo.firstName,
            link: connect?.link,
            token: connect?.token,
            logoUrl,
            host: window.location.host
        });
        const resultMessage = updatedMessage.replace(/ ,/g, ',');
        setMessage(resultMessage);
        setInitialMessage(resultMessage);
        setErrorMessage(null);
    }, [connect, logoUrl]);

    const disableThrottle = useCallback(() => {
        setThrottlingDate(null);
        setErrorMessage(null);
    }, []);

    const onToggleSchedule = useCallback(() => {
        const isScheduledValue = !isScheduled;
        setScheduledDate(isScheduledValue ? dateUtils.getNextMinutesDate(timeZone, 5) : null);
        disableThrottle();
    }, [isScheduled, timeZone, disableThrottle]);

    const handleMessageNameChange = useCallback((name: ClientConnectMessageName) => {
        setMessageName(name);
    }, []);

    const handleClose = useCallback((confirm: boolean) => {
        if (confirm) {
            setErrorMessage('');

            if (notificationType === ClientConnectNotificationType.SMS && message.length > SMS_MAX_LENGTH) {
                setErrorMessage(T_ERROR_KEYS.MAX_LENGTH_EXCEEDED);
                return;
            }

            if (isScheduled && scheduledDate && scheduledDate.isBefore(moment.tz(timeZone))) {
                setErrorMessage(T_ERROR_KEYS.INVALID_DATE);
                return;
            }

            sendMessage(connect?.id, notificationType, {
                message,
                subject,
                messageName,
                ...(scheduledDate && {scheduledDate: scheduledDate.toISOString()})
            });
        } else {
            closeModal(false);
        }
    }, [isScheduled, connect, subject, message, notificationType, messageName, scheduledDate, timeZone, closeModal, sendMessage]);

    useEffect(() => {
        setNotificationType(defaultNotificationType);
    }, [defaultNotificationType]);

    useEffect(() => {
        setSubject(messageSubject);
    }, [messageSubject]);

    useEffect(() => {
        if (messageTemplate) {
            refreshMessage(messageTemplate);
        }
    }, [refreshMessage, messageTemplate]);

    useEffect(() => {
        if (isNotificationTypeChanged) {
            setScheduledDate(null);
            setErrorMessage(null);
        }
    }, [isNotificationTypeChanged]);

    useEffect(() => {
        if (!isEmpty(messages) && isNotificationTypeChanged) {
            const [{template: tmpl, name}] = messages;
            refreshMessage(tmpl);
            setMessageName(name);
        }
    }, [messages, refreshMessage, isNotificationTypeChanged]);

    return (
        <ConfirmModal
            open={open}
            title={t(T_KEYS.TITLE)}
            fullWidth
            maxWidth='sm'
            okDisabled={loading || !!throttlingDate}
            okText={t(T_KEYS.SUBMIT_BTN)}
            onClose={handleClose}
        >
            <form>
                {errorMessage &&
                    <div className='tw-mb-8'>
                        <div className='tw-flex tw-items-center tw-text-alert'>
                            <Icon name='info' color='alert' className='tw-mr-2' />
                            {t(errorMessage)}
                        </div>
                        {throttlingDate &&
                            <div className='tw-flex tw-items-center tw-mt-2'>
                                <Icon name='clock' className='tw-mr-2' />
                                <Timer
                                    endDate={throttlingDate}
                                    startDateNow
                                    onTimeOver={disableThrottle}
                                />
                            </div>}
                    </div>}

                {recipientDetails &&
                    <div>
                        <span className='tw-ts-base'>{`${t(T_KEYS.RECIPIENT)}: `}</span>
                        <span className='tw-ts-base-bold'>{recipientDetails}</span>
                    </div>}
                <div className='tw-flex tw-items-center'>
                    <Select
                        value={selectedNotificationTypeOption}
                        options={notificationTypeOptions}
                        className='tw-flex-grow tw-mr-2 tw-w-1/4 -sm:tw-w-1/2'
                        placeholder={t(T_KEYS.NOTIFICATION_TYPE.LABEL)}
                        onChange={({id}) => {
                            disableThrottle();
                            setNotificationType(id);
                        }}
                    />

                    <Select
                        value={selectedMessageOption}
                        options={messageOptions}
                        className={cx('tw-my-4 tw-flex-grow tw-w-3/4 -sm:tw-w-1/2')}
                        placeholder={t(T_KEYS.TEMPLATE)}
                        onChange={({id}) => handleMessageNameChange(id)}
                    />
                </div>

                {notificationType === ClientConnectNotificationType.Email &&
                    <Input value={subject} label={t(T_KEYS.SUBJECT)} onChange={setSubject} />}

                {WITH_LOGO_MESSAGES.includes(messageName) &&
                    <FilesManagerInput
                        filesManagerProps={{
                            ownerType: FileOwner.Account,
                            area: FileArea.ClientConnectLogo
                        }}
                        inputProps={{
                            name: 'client-connect-logo',
                            label: t(T_KEYS.LOGO_URL),
                            value: logoUrl,
                            onChange: setLogoUrl,
                        }}
                        helperText={t(T_KEYS.TEMPLATE_HELPER)}
                        onFilePathSelect={setLogoUrl}
                    />}

                {[ClientConnectNotificationType.SMS, ClientConnectNotificationType.Whatsapp].includes(notificationType) &&
                    <div className='tw-flex tw-flex-col'>
                        <TextArea
                            required
                            name='message'
                            label={t(T_KEYS.TEXT_CONTENT)}
                            value={message}
                            rows={4}
                            hasError={message.length > SMS_MAX_LENGTH}
                            className='tw-mt-4'
                            maxLength={SMS_MAX_LENGTH}
                            disabled={notificationType === ClientConnectNotificationType.Whatsapp}
                            onChange={setMessage}
                        />
                        <CopyText text={message} copiedLabelOnly className='tw-self-end'>
                            <Button
                                Icon={<Icon className='tw-mr-2' name='copy' />}
                            >
                                {t(T_KEYS.COPY_BTN)}
                            </Button>
                        </CopyText>
                    </div>}
                {notificationType === ClientConnectNotificationType.Email &&
                    <Wysiwyg
                        initialValue={initialMessage}
                        height={260}
                        label={t(T_KEYS.TEXT_CONTENT)}
                        className='tw-mt-4'
                        onChange={setMessage}
                    />}

                <div className='tw-flex tw-mt-4'>
                    <Checkbox
                        name='schedule'
                        className='tw-mr-2 tw-mt-4'
                        label={t(T_KEYS.SCHEDULE)}
                        checked={isScheduled}
                        onChange={onToggleSchedule}
                    />
                    {isScheduled &&
                        <DateTimePicker
                            value={scheduledDate}
                            timeZone={timeZone}
                            openDirection='top'
                            dateFormat={dateFormat}
                            timeFormat={timeFormat}
                            timeIncrement={5}
                            onChange={setScheduledDate}
                        />}
                </div>
            </form>
        </ConfirmModal>
    );
};
